import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const AdminTabAnalytics = () => {
  const { analyticsDays, analyticsLoading, contactAnalytics, contactMessages, setAnalyticsDays, updateMessageStatus } = useAdminCtx();
  return (
    <>
            <div className="space-y-6">
              {/* Time Period Selector */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-6 h-6 text-primary" />
                  <h3 className="font-semibold text-gray-900">Contact Analytics</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-500">Period:</Label>
                  <select
                    value={analyticsDays}
                    onChange={(e) => setAnalyticsDays(Number(e.target.value))}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                    data-testid="analytics-period-select"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : contactAnalytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {contactAnalytics.contact_form_submissions || 0}
                          </p>
                          <p className="text-xs text-gray-500">Form Submissions</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {contactAnalytics.support_messages || 0}
                          </p>
                          <p className="text-xs text-gray-500">Support Messages</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <MousePointerClick className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {contactAnalytics.event_breakdown?.email_click || 0}
                          </p>
                          <p className="text-xs text-gray-500">Email Clicks</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {contactAnalytics.insights?.avg_daily_contacts || 0}
                          </p>
                          <p className="text-xs text-gray-500">Avg Daily Contacts</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Method Comparison & Insights */}
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Method Comparison */}
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <h4 className="font-semibold text-gray-900 mb-4">Contact Method Comparison</h4>
                      <div className="space-y-4">
                        {Object.entries(contactAnalytics.method_comparison || {}).map(([method, count]) => {
                          const maxCount = Math.max(...Object.values(contactAnalytics.method_comparison || {})) || 1;
                          const percentage = Math.round((count / maxCount) * 100);
                          const isTop = method === contactAnalytics.most_popular_method;
                          
                          return (
                            <div key={method}>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm text-gray-600 capitalize flex items-center gap-2">
                                  {method.replace('_', ' ')}
                                  {isTop && (
                                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                      Most Popular
                                    </Badge>
                                  )}
                                </span>
                                <span className="text-sm font-medium">{count}</span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    isTop ? 'bg-emerald-500' : 'bg-primary'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Insights */}
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <h4 className="font-semibold text-gray-900 mb-4">Insights</h4>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                          <ArrowUpRight className="w-5 h-5 text-emerald-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-emerald-700">Preferred Method</p>
                            <p className="text-sm text-emerald-600 capitalize">
                              {contactAnalytics.most_popular_method?.replace('_', ' ') || 'No data yet'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-700">Analysis Period</p>
                            <p className="text-sm text-blue-600">
                              Last {contactAnalytics.period_days} days ({contactAnalytics.total_events} total events)
                            </p>
                          </div>
                        </div>
                        {contactAnalytics.insights?.peak_source && (
                          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                            <TrendingUp className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-amber-700">Top Traffic Source</p>
                              <p className="text-sm text-amber-600 capitalize">
                                {contactAnalytics.insights.peak_source.replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recent Contact Messages */}
                  <div className="bg-white rounded-xl p-6 border border-gray-100">
                    <h4 className="font-semibold text-gray-900 mb-4">Recent Contact Form Submissions</h4>
                    {contactMessages.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Subject</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contactMessages.map((msg) => (
                              <tr key={msg.contact_id}>
                                <td className="text-sm">{formatDate(msg.created_at)}</td>
                                <td className="font-medium">{msg.name}</td>
                                <td className="text-sm">{msg.email}</td>
                                <td className="text-sm max-w-xs truncate">{msg.subject}</td>
                                <td>
                                  <Badge className={getStatusBadgeClass(
                                    msg.status === 'new' ? 'pending' : 
                                    msg.status === 'replied' ? 'active' : 
                                    msg.status === 'resolved' ? 'active' : 'info'
                                  )}>
                                    {msg.status}
                                  </Badge>
                                </td>
                                <td>
                                  <select
                                    value={msg.status}
                                    onChange={(e) => updateMessageStatus(msg.contact_id, e.target.value)}
                                    className="text-xs border rounded px-2 py-1"
                                  >
                                    <option value="new">New</option>
                                    <option value="read">Read</option>
                                    <option value="replied">Replied</option>
                                    <option value="resolved">Resolved</option>
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No contact form submissions yet</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No analytics data available
                </div>
              )}
            </div>
    </>
  );
};

export default AdminTabAnalytics;
